import {
  Card,
  CheckboxCard,
  Field,
  Flex,
  Grid,
  GridItem,
  Text,
} from "@chakra-ui/react";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";

import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AppColors } from "@/styles/colors";
import { useNavigate } from "react-router-dom";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { TextArea } from "@/components/text-area/TextArea";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { AppRoutes } from "@/constants/routes";
import { useEffect, useMemo, useRef, useState } from "react";
import { handleTonChangeInInput, isEmpty } from "@/utils/query-string";
import {
  createFutureContractHandler,
  getOptionsHandler,
  loadOptionsHandler,
} from "../schema/handler-functions";
import { SelectComponent } from "@/components/select/Select";
import { getListadoGranos } from "@/services/aux-entities.service";
import { InputField } from "@/components/input-field/InputField";
import { numbersWithCommaDecimal } from "@/utils/reg-expressions";
import { ComboboxComponent } from "@/components/select/Combobox";
import { useRoles } from "@/hooks/useRoles";
import MonthYearPicker from "@/components/date-picker/month-year-picker";
import {
  GranoEntity,
} from "../../sell-orders/schema/sell-order.schema";
import { futureContractSchema } from "../schema/future-contract.schema";

import { CounterDeliveryHarvest } from "../../counter-delivery/schema/counter-delivery.schema";
import { getCounterDeliveryHarvests } from "@/services/counter-delivery.service";

export const FutureContractCreationPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  useEffect(() => {
    setIsActive(true);
  }, []);
  const navigate = useNavigate();
  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: zodResolver(futureContractSchema),
  });

  const [grains, setGrains] = useState<GranoEntity[] | null>([]);
   const [futureContractHarvest, setFutureContractHarvest] = useState<
     CounterDeliveryHarvest[] | null
   >([]);

  const [loadingGrains, setLoadingGrains] = useState<boolean>(false);



  const fetchGrains = async () => {
    setLoadingGrains(true);
    const response = await getListadoGranos();
    setGrains(response);
    setLoadingGrains(false);
  };

  useEffect(() => {
    if (isActive) {
      fetchGrains();
    }
  }, [isActive]);

  const handleCancel = () => {
    navigate(AppRoutes.GRAIN_FUTURE_CONTRACTS);
  };

  const { fetchPermissions } = useRoles();

  const [loadingCreateFutureContract, setLoadingCreateFutureContract] =
    useState<boolean>(false);

  const [loadingPersons, setLoadingPersons] = useState<boolean>(false);
  const watchedValues = useWatch({ control });

  const [loadingFutureContractHarvests, setLoadingFutureContractHarvests] = useState<boolean>(false)

  const fetchFutureContractHarvests = async () => {
    setLoadingFutureContractHarvests(true);
    const response = await getCounterDeliveryHarvests();
    setFutureContractHarvest(response);
    setLoadingFutureContractHarvests(false);
  };

  useEffect(() => {
    if (isActive) {
      fetchFutureContractHarvests();
    }
  }, [isActive])

  const getOptions = async (
    inputValue: string,
  ): Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  > => {
    return getOptionsHandler({
      inputValue,
    });
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions,
      inputValue,
      setLoadingPersons,
      timeoutRef,
    });
  };

  const handleCreateSellOrder = async () => {
    await createFutureContractHandler({
      navigate,
      setLoadingCreateFutureContract,
      watchedValues,
      fetchPermissions,
    });
  };

  const hasNoFilledValues = useMemo(() => {
    const { account, delivery_month, grain, harvest, ton } =
      watchedValues || {};

    return (
      isEmpty(account) ||
      isEmpty(delivery_month) ||
      isEmpty(grain) ||
      isEmpty(harvest) ||
      isEmpty(ton) ||
      Number(watchedValues?.ton) > Number(watchedValues?.available_ton)
    );
  }, [watchedValues]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar venta de granos a futuro"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateSellOrder}
        disableAdd={hasNoFilledValues}
        loading={loadingCreateFutureContract}
      />
      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 3fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <Field.Root invalid={!!errors?.account?.message} gap="12px">
                <Field.Label>
                  {" "}
                  <Text fontSize="16px" fontWeight={600}>
                    Cuenta
                  </Text>
                </Field.Label>

                <AsyncAutocompleteSelect
                  control={control}
                  loadOptions={loadOptions}
                  name="account"
                  placeholder="Buscar..."
                  isLoading={loadingPersons}
                  disabled={loadingCreateFutureContract}
                />

                <Field.ErrorText>
                  {typeof errors?.account?.message === "string"
                    ? errors.account.message
                    : ""}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>
            <GridItem paddingY="8px" paddingX="16px">
              <Field.Root
                invalid={!!errors?.delivery_month?.message}
                gap="12px"
              >
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>
                    Entrega
                  </Text>
                </Field.Label>
                <MonthYearPicker
                  name="delivery_month"
                  control={control}
                  disabled={loadingCreateFutureContract}
                  clearable
                  placeholder="Seleccionar mes y año"
                  required
                  maxMonthsAhead={16}
                  minMonthsFromNow={0}
                />
                <Field.ErrorText>
                  {typeof errors?.delivery_month?.message === "string"
                    ? errors.delivery_month.message
                    : ""}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>

            <GridItem paddingY="8px" paddingX="16px">
              <Field.Root invalid={!!errors?.grain?.message} gap="12px">
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>
                    Grano
                  </Text>
                </Field.Label>

                <ComboboxComponent
                  key={grains?.length ? `grain-loaded` : "grain-empty"}
                  items={
                    grains?.map((el) => ({
                      label: el?.nombre,
                      value: el?.id,
                    })) || []
                  }
                  menuMaxHeight="250px"
                  name="grain"
                  control={control}
                  clearable
                  placeholder="Seleccionar grano"
                  loading={loadingGrains}
                  disabled={loadingCreateFutureContract || loadingGrains}
                  fullWidth
                />

                <Field.ErrorText>
                  {typeof errors?.grain?.message === "string"
                    ? errors.grain.message
                    : ""}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>

            <GridItem paddingY="8px" paddingX="16px">
              <Field.Root invalid={!!errors?.harvest?.message}>
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>
                    Cosecha
                  </Text>
                </Field.Label>
                <SelectComponent
                  fullWidth
                  items={
                    futureContractHarvest?.map((el) => ({
                      label: `${el.cosecha.slice(0, 2)}-${el.cosecha.slice(2)}`,
                      value: el.cosecha,
                    })) || []
                  }
                  menuMaxHeight="250px"
                  name="harvest"
                  control={control}
                  closeOnSelect
                  clearable
                  size="xs"
                  minHeight="40px"
                  placeholder="Seleccionar cosecha"
                  loading={loadingFutureContractHarvests}
                  disabled={
                    loadingCreateFutureContract ||
                    loadingFutureContractHarvests ||
                    Boolean(!futureContractHarvest?.length)
                  }
                />
                <Field.ErrorText>
                  {typeof errors?.harvest?.message === "string"
                    ? errors.harvest.message
                    : ""}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>

            <GridItem paddingY="8px" paddingX="16px">
              <InputField
                inputName="ton"
                labelText="Toneladas"
                control={control}
                allowedPattern={numbersWithCommaDecimal}
                type="text"
                onChange={handleTonChangeInInput}
                disabled={loadingCreateFutureContract}
                placeholder="Ingrese toneladas"
              />
            </GridItem>

            <GridItem paddingY="8px" paddingX="16px">
              <ComboboxComponent
                label="Tipo de moneda"
                items={[
                  {
                    label: "Dólares",
                    value: "USD",
                  },
                  {
                    label: "Pesos",
                    value: "ARS",
                  },
                ]}
                menuMaxHeight="250px"
                name="currency_type"
                control={control}
                clearable
                placeholder="Seleccionar tipo de moneda"
                fullWidth
                disabled={loadingCreateFutureContract}
              />
            </GridItem>

            <GridItem paddingX="16px" paddingY="2px">
              <Field.Root invalid={!!errors?.price?.message} gap="12px">
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>
                    Precio
                  </Text>
                </Field.Label>
                <InputCurrency
                  control={control}
                  name="price"
                  height="40px"
                  maxLength={20}
                  disabled={loadingCreateFutureContract}
                />

                <Field.ErrorText>
                  {typeof errors?.price?.message === "string"
                    ? errors.price.message
                    : ""}
                </Field.ErrorText>
              </Field.Root>
            </GridItem>

            <GridItem paddingX="16px" paddingY="30px">
              <CheckboxCard.Root
                maxW="240px"
                disabled={loadingCreateFutureContract}
              >
                <CheckboxCard.HiddenInput
                  value={watchedValues.isExchange ? "on" : "off"}
                  onChange={(e) => setValue("isExchange", e.target.checked)}
                />
                <CheckboxCard.Control paddingX="16px" paddingY="12px">
                  <CheckboxCard.Label>
                    {" "}
                    <Text fontSize="16px" fontWeight={600}>
                      Es canje
                    </Text>
                  </CheckboxCard.Label>
                  <CheckboxCard.Indicator />
                </CheckboxCard.Control>
              </CheckboxCard.Root>
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>
      <Flex flexDir="column" gap="12px">
        <Text fontWeight={700} fontSize="20px">
          Detalle
        </Text>
        <TextArea
          errors={errors}
          inputName="contract_detail"
          maxLength={50}
          register={register}
          placeholder="Ingresar una descripción de la solicitud"
          helperText="Máximo 50 caracteres"
          disabled={loadingCreateFutureContract}
        />
      </Flex>
    </Flex>
  );
};
